import React, { useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Seo from './Seo';
import KwbLogo from './KwbLogo';
import { publishedBlogPosts } from '../generated/blogPosts';

const { FiArrowLeft, FiArrowRight, FiLinkedin } = FiIcons;

const weaveArticle = (html, media = []) => {
  const sections = html.split(/(?=<h2>)/);
  if (!media.length || sections.length < 2) return [{ html, media: null }];
  const targets = media.map((_, index) => Math.min(sections.length - 1, Math.max(1, Math.round(((index + 1) * sections.length) / (media.length + 1)))));
  return sections.map((section, index) => ({ html: section, media: media[targets.indexOf(index)] || null }));
};

const BlogPostPage = ({ post, reviewMode = false }) => {
  const index = publishedBlogPosts.findIndex((item) => item.slug === post.slug);
  const previous = publishedBlogPosts.length > 1 ? publishedBlogPosts[(index - 1 + publishedBlogPosts.length) % publishedBlogPosts.length] : null;
  const next = publishedBlogPosts.length > 1 ? publishedBlogPosts[(index + 1) % publishedBlogPosts.length] : null;
  const socialImage = post.seo.socialImage || post.cover;
  const articleSections = weaveArticle(post.html, post.media);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.seo.description,
    image: `https://kwbotrous.com${socialImage}`,
    datePublished: post.date,
    dateModified: post.updated,
    mainEntityOfPage: `https://kwbotrous.com/blog/${post.slug}/`,
    author: {
      '@type': 'Person',
      '@id': 'https://kwbotrous.com/#katherine-william-botrous',
      name: post.author,
      url: 'https://kwbotrous.com',
    },
    keywords: [post.category, ...post.topics].join(', '),
    ...(post.faqs.length ? {
      hasPart: {
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    } : {}),
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post.slug]);

  return (
    <div className="min-h-screen bg-white text-black">
      <Seo
        title={`${post.seo.title} | Katherine William-Botrous`}
        description={post.seo.description}
        path={`/blog/${post.slug}/`}
        image={socialImage}
        schema={schema}
        pageType="article"
        noIndex={false}
      />

      <header className="bg-black text-white border-b border-white/15 px-5 md:px-10 py-6 md:py-8">
        <nav aria-label="Article navigation" className="max-w-[1600px] mx-auto flex items-center justify-between gap-5">
          <a href="/blog/" className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors">
            <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
            All articles
          </a>
          <a href="/" className="inline-flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#e2c96e]">
            <KwbLogo variant="white" className="h-9 w-auto md:h-10" />
          </a>
        </nav>
      </header>

      <main>
        <article>
          <header className="bg-black text-white px-5 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
            <div className="max-w-[1400px] mx-auto">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#e2c96e]">{post.category}</div>
              <h1 className="blog-article-title mt-7 max-w-6xl text-[clamp(2.65rem,8vw,8.5rem)] font-black uppercase tracking-[-0.055em] leading-[0.84]">{post.title}</h1>
              {post.subtitle && <p className="mt-8 max-w-4xl text-xl md:text-3xl leading-tight text-white/80">{post.subtitle}</p>}
              <div className="mt-12 pt-7 border-t border-white/20 flex flex-wrap gap-x-8 gap-y-3 text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                <span>{post.author}</span>
                <time dateTime={post.date}>{post.date}</time>
                <span>{post.readingMinutes} min read</span>
              </div>
            </div>
          </header>

          <figure className="bg-black">
            <img src={post.cover} alt={post.coverAlt} className="w-full max-h-[82vh] object-cover" />
            {post.coverCredit && <figcaption className="px-5 md:px-10 py-3 text-[10px] uppercase tracking-[0.14em] text-white/50">{post.coverCredit}</figcaption>}
          </figure>

          <div className="px-5 md:px-10 py-16 md:py-24">
            <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <aside className="lg:col-span-3">
                <div className="lg:sticky lg:top-10 border-t border-black pt-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#918147]">In this perspective</div>
                  <p className="mt-5 text-lg leading-relaxed text-[#4a4a4a]">{post.excerpt}</p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {post.topics.map((topic) => <span key={topic} className="border border-black px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em]">{topic}</span>)}
                  </div>
                </div>
              </aside>
              <div className="lg:col-span-8 lg:col-start-5 blog-prose">
                {articleSections.map((section, sectionIndex) => (
                  <React.Fragment key={`${post.slug}-${sectionIndex}`}>
                    {/* Every HTML fragment was sanitized at build time with the strict allowlist in scripts/build-content.mjs. */}
                    <div className="blog-prose-section" dangerouslySetInnerHTML={{ __html: section.html }} />
                    {section.media && (
                      <figure className="blog-editorial-media border-t border-black pt-6">
                        <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#918147]">{section.media.role}</div>
                        <img src={section.media.image} alt={section.media.alt} loading="lazy" width="1440" height="810" className="w-full aspect-video object-cover bg-[#e5e0cf]" />
                        <figcaption className="mt-4 text-sm leading-relaxed text-[#4a4a4a]">
                          <span>{section.media.caption}</span>
                          {section.media.credit && <span className="block mt-2 text-[10px] uppercase tracking-[0.13em] text-[#777]">{section.media.credit}</span>}
                        </figcaption>
                      </figure>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {post.faqs.length > 0 && (
            <section className="bg-[#e2c96e] px-5 md:px-10 py-20 md:py-28">
              <div className="max-w-[1280px] mx-auto">
                <div className="text-xs font-bold uppercase tracking-[0.2em]">Questions readers ask</div>
                <h2 className="mt-5 text-5xl md:text-8xl font-black uppercase tracking-tighter leading-[0.82]">Direct answers</h2>
                <div className="mt-12 border-t border-black">
                  {post.faqs.map((faq, faqIndex) => (
                    <div key={faq.question} className="grid grid-cols-[2.5rem_1fr] md:grid-cols-[5rem_1fr] gap-4 md:gap-8 py-9 border-b border-black">
                      <div className="text-xs font-bold">/ {String(faqIndex + 1).padStart(2, '0')}</div>
                      <div>
                        <h3 className="text-2xl md:text-4xl font-black uppercase tracking-tight">{faq.question}</h3>
                        <p className="mt-5 text-base md:text-xl leading-relaxed max-w-4xl">{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {post.sources?.length > 0 && (
            <section className="px-5 md:px-10 py-20 md:py-24 bg-[#f6f4ec] border-t border-black">
              <div className="max-w-[1280px] mx-auto">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#918147]">Evidence and further reading</div>
                <h2 className="mt-5 text-4xl md:text-7xl font-black uppercase tracking-tighter leading-[0.86]">Sources behind the perspective</h2>
                <ol className="mt-12 border-t border-black">
                  {post.sources.map((source, sourceIndex) => (
                    <li key={source.url} className="grid grid-cols-[2.5rem_1fr] md:grid-cols-[5rem_1fr] gap-4 md:gap-8 py-7 border-b border-black">
                      <span className="text-xs font-bold">/ {String(sourceIndex + 1).padStart(2, '0')}</span>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#71642f]">{source.publisher}</div>
                        <a href={source.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 text-lg md:text-2xl font-black underline underline-offset-4 decoration-1 hover:text-[#71642f]">
                          {source.title} <SafeIcon icon={FiArrowRight} />
                        </a>
                        {source.supports && <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-[#4a4a4a]">Supports: {source.supports}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          )}
        </article>

        <section className="bg-black text-white px-5 md:px-10 py-20 md:py-24">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-14">
              <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tight">Continue the conversation.</h2>
              <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#e2c96e] text-black rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.16em]">
                LinkedIn <SafeIcon icon={FiLinkedin} />
              </a>
            </div>
            {(previous || next) && (
              <div className="grid grid-cols-1 md:grid-cols-2 border-l border-white/30">
                {previous && <a href={`/blog/${previous.slug}/`} className="group border-r border-y border-white/30 p-7 md:p-10"><div className="text-xs uppercase tracking-[0.18em] text-white/50">Previous article</div><div className="mt-5 flex justify-between gap-5 text-2xl md:text-4xl font-black uppercase"><span>{previous.title}</span><SafeIcon icon={FiArrowLeft} /></div></a>}
                {next && <a href={`/blog/${next.slug}/`} className="group border-r border-y border-white/30 p-7 md:p-10"><div className="text-xs uppercase tracking-[0.18em] text-white/50">Next article</div><div className="mt-5 flex justify-between gap-5 text-2xl md:text-4xl font-black uppercase"><span>{next.title}</span><SafeIcon icon={FiArrowRight} /></div></a>}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default BlogPostPage;
